import { Box, Button, Grid2, Stack } from "@mui/material";
import { CustomTextView } from "../../../component/CustomTextView";
import { CheckCircleRounded, LockRounded } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import {
  COLOR_BLACK,
  COLOR_GREEN,
  COLOR_LIGHT_GRAY,
  COLOR_MAIN,
  COLOR_WHITE,
} from "../../../../data/colors/Colors";
import { useTranslation } from "react-i18next";
import { Category, Subcategory } from "../../../../domain/model/Category";
import { CustomImageView } from "../../../component/CustomImageView";
import { CustomCardView } from "../../../component/CustomCardView";
import useWindowDimensions from "../../../../core/utils/UseWindowDimensions";
import { CustomPopoverView } from "../../../component/CustomPopoverView";

import useDoubleClick from "use-double-click";
import { ChooseCategoryDetailsView } from "./ChooseCategoryDetailsView";
import { LoadingView } from "../../../component/LoadingView";
import { Player } from "../../../../domain/model/User";
import {
  getUserInfoAgain,
  isCategoryLocked,
} from "../../../../core/utils/Utils";
import { UnlockCategoryView } from "./unlockCategory/UnlockCategoryView";

export function ChooseCategoriesView({
  player,
  categories,
  selectedCategory,
  setSelectedCategory,
  loading,
  onCategoryClicked,
  onCategoryDoubleClicked,
  onSubcategoryClicked,
  shouldChooseCategoryDirectly,
}: {
  player: Player;
  categories: Array<Category>;
  selectedCategory: Category | undefined;
  setSelectedCategory: (category: Category) => void;
  loading: boolean;
  onCategoryClicked: (category: Category, closePopover?: () => void) => void;
  onCategoryDoubleClicked: (category: Category) => void;
  onSubcategoryClicked: (category: Category, subcategory: Subcategory) => void;
  shouldChooseCategoryDirectly: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() == "rtl";
  const { width } = useWindowDimensions();
  const [anchorElPosition, setAnchorElPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const [categoriesNumbersUpdated, setCategoriesNumbersUpdated] =
    useState(false);

  useEffect(() => {
    if (
      !categoriesNumbersUpdated &&
      categories &&
      categories.filter(
        (c) => c.numberOfQuestionsForEachSubcategory != undefined
      ).length > 0 &&
      selectedCategory
    ) {
      setSelectedCategory(
        categories.filter((c) => c.id == selectedCategory.id)[0]
      );
      setCategoriesNumbersUpdated(true);
    }
  }, [categories]);

  return (
    <Stack
      spacing={2}
      width={"100%"}
      height={"100%"}
      sx={{ overflow: "auto" }}
      p={width < 700 ? 1.5 : 2}
    >
      <Stack>
        <CustomTextView
          text={t("choose_max_6_categories")}
          fontWeight="bold"
          variant="h6"
        />
        <CustomTextView
          text={t(
            "double_tap_on_each_category_to_select_all_its_subcategories"
          )}
          variant="subtitle2"
          textColor={COLOR_LIGHT_GRAY}
        />
      </Stack>

      {loading ? (
        <LoadingView minHeight="50vh" />
      ) : (
        <Grid2 container sx={{ rowGap: 1 }}>
          {categories.map((category, index) => {
            const isCategoryFullyLocked = isCategoryLocked(
              player,
              category.id,
              category.locked
            );
            return (
              <Grid2
                size={6}
                sx={{
                  pr: index % 2 == 0 ? (isRtl ? 0 : 0.5) : isRtl ? 0.5 : 0,
                  pl: index % 2 != 1 ? (isRtl ? 0.5 : 0) : isRtl ? 0 : 0.5,
                }}
                key={category.id}
              >
                <CategoryItemView
                  category={category}
                  player={player}
                  onCategoryClicked={(e, category) => {
                    if (shouldChooseCategoryDirectly) {
                      category.subcategories.map((s) => {
                        s.isSelected = !category.isSelected;
                      });
                      onCategoryClicked(category);
                      return;
                    }
                    setSelectedCategory(category);
                    setAnchorElPosition({ top: e.clientY, left: e.clientX });
                  }}
                  onCategoryDoubleClicked={(e, category) => {
                    if (isCategoryFullyLocked) {
                      return;
                    }
                    if (shouldChooseCategoryDirectly) return;
                    setSelectedCategory(category);
                    onCategoryDoubleClicked(category);
                    setAnchorElPosition({ top: e.clientY, left: e.clientX });
                  }}
                />
              </Grid2>
            );
          })}
        </Grid2>
      )}

      {selectedCategory && (
        <CustomPopoverView
          onClose={() => {
            setAnchorElPosition(null);

            if (
              isCategoryLocked(
                player,
                selectedCategory!.id,
                selectedCategory!.locked
              )
            ) {
              getUserInfoAgain();
            }
          }}
          anchorElPosition={anchorElPosition}
          layout={
            <>
              {isCategoryLocked(
                player,
                selectedCategory!.id,
                selectedCategory!.locked
              ) ? (
                <UnlockCategoryView
                  category={selectedCategory}
                  handleClose={() => {
                    setAnchorElPosition(null);
                    getUserInfoAgain();
                  }}
                />
              ) : (
                <ChooseCategoryDetailsView
                  category={selectedCategory!}
                  onCategoryClicked={onCategoryClicked}
                  onSubcategoryClicked={onSubcategoryClicked}
                  onPopoverClose={() => setAnchorElPosition(null)}
                />
              )}
            </>
          }
        />
      )}
    </Stack>
  );
}

function CategoryItemView({
  category,
  onCategoryClicked,
  onCategoryDoubleClicked,
  player,
}: {
  category: Category;
  player: Player;
  onCategoryClicked: (
    e: React.MouseEvent<HTMLButtonElement | Element>,
    category: Category
  ) => void;
  onCategoryDoubleClicked: (
    e: React.MouseEvent<HTMLButtonElement | Element>,
    category: Category
  ) => void;
}) {
  const buttonRef = useRef(null);

  useDoubleClick({
    onSingleClick: (e) => {
      // if (notAllowedToChoose) return;
      onCategoryClicked(e, category);
    },
    onDoubleClick: (e) => {
      // if (notAllowedToChoose) return;
      onCategoryDoubleClicked(e, category);
    },
    ref: buttonRef,
    latency: 250,
  });

  // var numberOfGamesLeft = -1;
  // if (category.numberOfQuestionsForEachSubcategory != null) {
  //   numberOfGamesLeft = Math.round(
  //     category.numberOfQuestionsForEachSubcategory.reduce(
  //       (accumulator, currentValue) =>
  //         accumulator + currentValue.totalQuestions,
  //       0
  //     ) / 6
  //   );
  // }

  // const notAllowedToChoose = numberOfGamesLeft == 0;

  const isCategoryFullyLocked = isCategoryLocked(
    player,
    category.id,
    category.locked
  );

  return (
    <CustomCardView
      padding={0}
      height={200}
      background={COLOR_WHITE}
      border={
        !category.isSelected ? "solid 1px black" : `solid 4px ${COLOR_GREEN}`
      }
      children={
        <Button
          sx={{ p: 0, textTransform: "none", height: "100%" }}
          ref={buttonRef}
          fullWidth
          // disabled={notAllowedToChoose}
        >
          <Stack
            spacing={0}
            height={"100%"}
            width={"100%"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Stack sx={{ height: "100%", width: "100%", position: "relative" }}>
              <CustomImageView
                src={category.imageUrl}
                width={"100%"}
                height={"100%"}
                props={{
                  // opacity: isCategoryFullyLocked ? 0.2 : 1,
                }}
              />
            </Stack>
            {isCategoryFullyLocked && (
              <Stack position={"absolute"} right={0} p={1} zIndex={11}>
                <LockRounded sx={{ color: COLOR_BLACK }} fontSize="large" />
              </Stack>
            )}

            {isCategoryFullyLocked && (
              <Stack
                width={"100%"}
                position={"absolute"}
                height={"100%"}
                zIndex={10}
                bgcolor={"rgba(0,0,0,0.3)"}
              />
            )}
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              justifyContent={"center"}
              width={"100%"}
              bgcolor={
                isCategoryFullyLocked
                  ? "rgba(0,0,0,0.5)"
                  : "rgba(135,40,77,0.8)"
              }
              position={"absolute"}
              bottom={0}
              p={0.5}
            >
              <CustomTextView
                text={category.title}
                variant="body1"
                fontWeight="bold"
                textColor="white"
              />
              {category.isSelected && (
                <CheckCircleRounded sx={{ color: COLOR_GREEN }} />
              )}
            </Stack>
          </Stack>
        </Button>
      }
    />
  );
}
